import React from 'react';
import type { Plan } from '@court-sync/shared';
import { Button } from '../ui';
import {
  COMPACT_FEATURE_PREVIEW_COUNT,
  getFullFeaturesForPlan,
  getPreviewFeaturesForPlan,
} from './planFeatureUtils';

export type PlanSelectionAccent = 'academy' | 'club';

export interface PlanSelectionCardProps {
  plan: Plan;
  priceLabel: string;
  isCurrentPlan: boolean;
  ctaLabel: string;
  onSelect: () => void;
  onViewFeatures: (plan: Plan) => void;
  disabled?: boolean;
  accent?: PlanSelectionAccent;
  showTrialBadge?: boolean;
  ctaClassName?: string;
  ctaVariant?: 'default' | 'outline';
}

const accentStyles: Record<
  PlanSelectionAccent,
  { currentBorder: string; currentBg: string; badge: string; hoverBorder: string }
> = {
  academy: {
    currentBorder: 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20',
    currentBg: 'bg-blue-500',
    badge: 'Current Plan',
    hoverBorder: 'hover:border-blue-300',
  },
  club: {
    currentBorder: 'border-2 border-violet-500 bg-violet-50 dark:bg-violet-900/20',
    currentBg: 'bg-violet-500',
    badge: 'Current Plan',
    hoverBorder: 'hover:border-violet-300',
  },
};

export function PlanSelectionCard({
  plan,
  priceLabel,
  isCurrentPlan,
  ctaLabel,
  onSelect,
  onViewFeatures,
  disabled = false,
  accent = 'academy',
  showTrialBadge = false,
  ctaClassName = '',
  ctaVariant,
}: PlanSelectionCardProps) {
  const styles = accentStyles[accent];
  const previewFeatures = getPreviewFeaturesForPlan(plan);
  const allFeatures = getFullFeaturesForPlan(plan);
  const hasMoreFeatures = allFeatures.length > COMPACT_FEATURE_PREVIEW_COUNT;

  const resolvedVariant = ctaVariant ?? (isCurrentPlan ? 'outline' : 'default');

  return (
    <div
      className={`relative flex flex-col rounded-lg border p-6 transition-colors ${
        isCurrentPlan
          ? styles.currentBorder
          : `border-gray-200 dark:border-gray-700 ${styles.hoverBorder}`
      }`}
    >
      {isCurrentPlan ? (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
          <span
            className={`rounded-full px-3 py-1 text-sm font-medium text-white ${styles.currentBg}`}
          >
            {styles.badge}
          </span>
        </div>
      ) : null}

      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h3>
        <div className="mt-2">
          <span className="text-3xl font-bold text-gray-900 dark:text-white">{priceLabel}</span>
          <span className="text-gray-600 dark:text-gray-400">/month</span>
        </div>
        {showTrialBadge ? (
          <div className="mt-2">
            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
              14-day Free Trial
            </span>
          </div>
        ) : null}
        {plan.description ? (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{plan.description}</p>
        ) : null}
      </div>

      <ul className="mb-2 mt-6 flex-1 space-y-1.5">
        {previewFeatures.map((feature) => (
          <li key={feature} className="flex items-center">
            <div className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green-100">
              <span className="text-[10px] font-bold leading-none text-green-600">✓</span>
            </div>
            <span className="text-xs text-gray-700 dark:text-gray-300">{feature}</span>
          </li>
        ))}
      </ul>

      {hasMoreFeatures ? (
        <button
          type="button"
          className="mb-4 text-left text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          onClick={() => onViewFeatures(plan)}
        >
          View all features ({allFeatures.length})
        </button>
      ) : null}

      <Button
        variant={resolvedVariant}
        className={`mt-auto h-auto w-full whitespace-normal py-3 ${ctaClassName}`}
        onClick={onSelect}
        disabled={disabled}
      >
        {ctaLabel}
      </Button>
    </div>
  );
}
